import { CheckCircle, HelpCircle, Mail, User } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable } from "react-native";
import { type SheetProps, XStack, YStack } from "tamagui";
import { AccountSheetActive } from "../../plans/PlanModel";
import { Body } from "../../ui/Body";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/InputNew";
import { Pill, PillType } from "../../ui/Pill";
import { Sheet } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { Title } from "../../ui/Title";
import { accountForgotPassword } from "../firebaseInit";
import { trackEvent } from "../trackEvent";

export const AccountForgotPasswordSheet = observer(
  ({
    open,
    onChangeSheet,
    ...props
  }: {
    onChangeSheet?: (sheet: AccountSheetActive) => void;
  } & SheetProps) => {
    const [email, setEmail] = React.useState("");

    const [isDone, setIsDone] = React.useState(false);

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<null | Error>(null);

    const sendEmail = React.useCallback(async () => {
      const e = email.trim();
      if (e) {
        setError(null);
        setLoading(true);
        try {
          await accountForgotPassword(e);
          setIsDone(true);
          trackEvent("account_forgot_password");
        } catch (e: unknown) {
          if (e instanceof Error) setError(e);
          trackEvent("account_forgot_passwordfailed");
        } finally {
          setLoading(false);
        }
      }
    }, [email]);

    return (
      <Sheet
        isLoading={loading}
        sheetId="account_forgot"
        open={open}
        onOpenChange={() => {
          onChangeSheet?.(AccountSheetActive.None);
        }}
        {...props}
        header={
          <YStack gap="$space.medium">
            <XStack gap="$medium" alignItems="center">
              <HelpCircle />
              <Title>Forgot password?</Title>
            </XStack>
            <Subtext>Don't worry, enter your email to set a new one.</Subtext>
          </YStack>
        }
      >
        <YStack gap="$space.medium">
          {error && <Pill type={PillType.Danger}>{error.message}</Pill>}

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Mail />
            <Input
              disabled={loading || isDone}
              autoComplete={"email"}
              keyboardType={"email-address"}
              inputMode={"email"}
              preventNewLine
              defaultValue={""}
              multiline={false}
              type="border"
              placeholder={"Your email..."}
              onChangeText={(text) => {
                setEmail(text);
              }}
              onSubmitEditing={sendEmail}
            />
          </XStack>

          <Pressable
            onPress={() => {
              onChangeSheet?.(AccountSheetActive.Login);
            }}
          >
            <Body muted>
              Back to{" "}
              <Body muted textDecorationLine="underline">
                Login?
              </Body>
            </Body>
          </Pressable>

          {isDone && (
            <XStack gap="$medium">
              <CheckCircle />
              <Body>Check your inbox to proceed!</Body>
            </XStack>
          )}

          <Button
            disabled={isDone}
            isLoading={loading}
            icon={User}
            onPress={sendEmail}
          >
            I'm ready!
          </Button>
        </YStack>
      </Sheet>
    );
  },
);
